// Adapted from pinpointedu/frontend/apps/product/src/config/apiOrigin.ts
// on 2026-09-05. This is the sole backend-origin authority for MoneySkyline:
// dev returns '' so Vite proxies root-relative /api and /media requests,
// production requires an explicit absolute origin or 'same-origin' setting.
// Both paths validate the resolved origin; missing configuration fails closed.

export interface ApiOriginConfig {
  dev: boolean;
  configuredUrl?: string;
  browserOrigin?: string;
}

const CONFIG_ERROR =
  "VITE_API_URL must be an absolute HTTP(S) origin or 'same-origin' with a valid browser origin; credentials, paths, queries, and fragments are forbidden";

/**
 * Hosts that may be reached over plaintext HTTP from a production build.
 *
 * The HTTPS requirement exists to stop a shipped bundle talking to a public
 * origin in the clear. These ranges are not routable on the public internet,
 * so that risk does not apply:
 *   - loopback;
 *   - 100.64.0.0/10, the CGNAT range Tailscale assigns. Tailscale carries
 *     every packet inside a WireGuard tunnel, so the transport is already
 *     encrypted and authenticated;
 *   - RFC1918 private ranges (10/8, 172.16/12, 192.168/16), for a LAN test;
 *   - *.ts.net, Tailscale MagicDNS names for the same tailnet.
 *
 * A public hostname over http:// still throws. Widen this only for another
 * range that is genuinely unroutable, never to silence an error.
 */
export function isPrivateHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  if (host.endsWith('.ts.net')) return true;

  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!v4) return false;
  const [a, b] = [Number(v4[1]), Number(v4[2])];
  const octets = [a, b, Number(v4[3]), Number(v4[4])];
  if (octets.some((n) => Number.isNaN(n) || n < 0 || n > 255)) return false;

  if (a === 127) return true; // loopback
  if (a === 10) return true; // RFC1918
  if (a === 172 && b >= 16 && b <= 31) return true; // RFC1918
  if (a === 192 && b === 168) return true; // RFC1918
  if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT / Tailscale
  return false;
}

export function resolveApiOrigin({ dev, configuredUrl, browserOrigin }: ApiOriginConfig): string {
  if (dev) return '';

  const configured = configuredUrl?.trim();
  const candidate = (configured === 'same-origin' ? browserOrigin : configured)?.trim().replace(/\/+$/, '') ?? '';
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error(CONFIG_ERROR);
  }

  const hasOnlyOrigin =
    (parsed.protocol === 'http:' || parsed.protocol === 'https:')
    && parsed.username === ''
    && parsed.password === ''
    && parsed.pathname === '/'
    && parsed.search === ''
    && parsed.hash === '';
  if (!hasOnlyOrigin) throw new Error(CONFIG_ERROR);

  if (parsed.protocol !== 'https:' && !isPrivateHost(parsed.hostname)) {
    throw new Error('HTTPS is required for non-local VITE_API_URL origins');
  }

  return parsed.origin;
}

export function resolveBackendUrl(pathOrUrl: string, origin: string): string {
  if (/^(?:https?:)?\/\//i.test(pathOrUrl)) {
    if (!origin || pathOrUrl.startsWith('//')) {
      throw new Error('Backend URL must use the configured API origin');
    }
    const parsed = new URL(pathOrUrl);
    if (parsed.origin !== origin) {
      throw new Error('Backend URL must use the configured API origin');
    }
    return parsed.toString();
  }
  if (/^(?:data|blob):/i.test(pathOrUrl)) {
    throw new Error('Backend URL must use the configured API origin');
  }
  if (!origin) return pathOrUrl;
  return `${origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Reject Axios targets that could override the owned client baseURL. */
export function assertOwnedBackendRequest(
  pathOrUrl: string | undefined,
  requestBaseUrl: string | undefined,
  ownedOrigin: string,
): void {
  if ((requestBaseUrl ?? '') !== ownedOrigin) {
    throw new Error('Request target must use the configured API origin');
  }
  if (!pathOrUrl) return;

  const trustedOrigin = ownedOrigin || globalThis.location?.origin;
  let resolved: URL;
  try {
    if (!trustedOrigin || trustedOrigin === 'null') throw new Error('missing API origin');
    resolved = new URL(pathOrUrl, `${trustedOrigin}/`);
  } catch {
    throw new Error('Request target must use the configured API origin');
  }
  if (
    (resolved.protocol !== 'http:' && resolved.protocol !== 'https:')
    || resolved.origin !== trustedOrigin
  ) {
    throw new Error('Request target must use the configured API origin');
  }
}

/**
 * The sole backend-origin authority. Development intentionally returns an
 * empty string so Vite can proxy root-relative API and media requests. Every
 * non-development runtime fails closed when its backend origin is absent or
 * malformed.
 */
export function getApiOrigin(): string {
  return resolveApiOrigin({
    dev: import.meta.env.DEV,
    configuredUrl: import.meta.env.VITE_API_URL,
    browserOrigin: globalThis.location?.origin,
  });
}

export const apiOrigin = getApiOrigin();
